import { useState, useEffect } from 'react';
import GlasVorschau3D from '../components/GlasVorschau3D';
import {
  DEFAULT_CONFIG, GLAS_OPTIONEN, RAHMEN_OPTIONEN, berechnePreis,
  type KonfiguratorState, type OeffnungsRichtung,
} from '../lib/konfigurator';

const fmt = (n: number) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);

const OEFFNUNG: [OeffnungsRichtung, string, string][] = [
  ['links',     '←', 'Links'],
  ['beidseitig','↔', 'Beidseitig'],
  ['rechts',    '→', 'Rechts'],
];

function calcScheiben(breite: number): number {
  const innerW = breite - 120;
  // Ab 6 Scheiben: nur 8 und 10 (7 und 9 werden übersprungen)
  const erlaubt = [2, 3, 4, 5, 6, 8, 10];
  let best = 2;
  let bestDiff = Infinity;
  for (const n of erlaubt) {
    const pw = innerW / n;
    if (pw >= 700 && pw <= 1050) {
      const diff = Math.abs(pw - 875);
      if (diff < bestDiff) { bestDiff = diff; best = n; }
    }
  }
  return best;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl shadow-[0_2px_20px_rgba(0,0,0,0.06)] border border-gray-100 overflow-hidden">
      <div className="px-5 pt-4 pb-1">
        <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400">{title}</h3>
      </div>
      <div className="px-5 pb-5 pt-3">{children}</div>
    </div>
  );
}

export default function Konfigurator() {
  const initScheiben = calcScheiben(DEFAULT_CONFIG.breite);
  const [cfg, setCfg] = useState<KonfiguratorState>({
    ...DEFAULT_CONFIG,
    scheibenAnzahl: initScheiben,
    spuren: 2,
  });
  const [ralNummer, setRalNummer] = useState('');
  const preis = berechnePreis(cfg);
  const upd = (patch: Partial<KonfiguratorState>) => setCfg(c => ({ ...c, ...patch }));

  useEffect(() => {
    const scheiben = calcScheiben(cfg.breite);
    setCfg(c => ({
      ...c,
      scheibenAnzahl: scheiben,
      spuren: 2,
      // Ab 600 cm nur beidseitig möglich
      oeffnungsRichtung: c.breite >= 5300 ? 'beidseitig' : c.oeffnungsRichtung,
    }));
  }, [cfg.breite]);

  const nurBeidseitig = cfg.breite >= 5300;

  const scheibenBreite = Math.round((cfg.breite - 120) / cfg.scheibenAnzahl);

  return (
    <div className="min-h-screen font-sans" style={{ background: '#f0f2f5' }}>

      {/* Header */}
      <header style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)' }}
        className="sticky top-0 z-20 shadow-xl">
        <div className="max-w-7xl mx-auto px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://skyagent-artifacts.skywork.ai/router/agent/2026-05-28/prod_agent_6e63ff6b-1c52-4935-a4c6-305f66688280/Logo_Pergolaland_563c6b446a8045889bc17f92632dbb54.png"
              alt="PergolaLand Logo" className="h-10 w-auto" />
            <div>
              <span className="text-white font-bold text-lg tracking-tight">PergolaLand</span>
              <span className="ml-2.5 text-xs font-medium px-2 py-0.5 rounded-full text-blue-200"
                style={{ background: 'rgba(59,130,246,0.2)' }}>
                Glasschiebewand-Konfigurator
              </span>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            {['🌍 DE/AT/CH'].map(b => (
              <span key={b} className="text-xs font-medium text-blue-200 px-3 py-1 rounded-full"
                style={{ background: 'rgba(255,255,255,0.08)' }}>{b}</span>
            ))}
          </div>
        </div>
      </header>

      {/* Hero-Titel */}
      <div className="max-w-7xl mx-auto px-5 pt-8 pb-4">
        <h1 className="text-2xl font-black text-gray-900 tracking-tight">
          Ihr Glasschiebewand <span style={{ color: '#3b82f6' }}>konfigurieren</span>
        </h1>
        <p className="text-sm text-gray-500 mt-1">Passen Sie Ihre Anlage individuell an — Preis wird in Echtzeit berechnet.</p>
      </div>

      <div className="max-w-7xl mx-auto px-5 pb-12 grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6">

        {/* Linke Spalte: 3D + Spezifikation */}
        <div className="space-y-5">

          {/* 3D Vorschau */}
          <div className="rounded-2xl overflow-hidden shadow-[0_4px_30px_rgba(0,0,0,0.12)] border border-gray-100"
            style={{ height: 440 }}>
            <GlasVorschau3D config={cfg} />
          </div>

          {/* Technische Spezifikation */}
          <Section title="Technische Spezifikation">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                ['Breite',         `${cfg.breite} mm`],
                ['Höhe',           `${cfg.hoehe} mm`],
                ['Scheiben',       `${cfg.scheibenAnzahl} Stk.`],
                ['Scheibenbreite', `${scheibenBreite} mm`],
                ['Fläche',         `${((cfg.breite/1000)*(cfg.hoehe/1000)).toFixed(2)} m²`],
                ['Öffnung',        cfg.oeffnungsRichtung.charAt(0).toUpperCase()+cfg.oeffnungsRichtung.slice(1)],
                ['Glastyp',        GLAS_OPTIONEN.find(g=>g.id===cfg.glasTyp)?.name ?? ''],
                ['Profil',         RAHMEN_OPTIONEN.find(r=>r.id===cfg.rahmenFarbe)?.name ?? ''],
              ].map(([k,v]) => (
                <div key={k} className="rounded-xl p-3" style={{ background: '#f8fafc' }}>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-0.5">{k}</div>
                  <div className="text-sm font-bold text-gray-800">{v}</div>
                </div>
              ))}
            </div>
          </Section>

          {/* Preisbox */}
          <div className="rounded-2xl overflow-hidden shadow-[0_4px_30px_rgba(15,23,42,0.25)]"
            style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1d4ed8 100%)' }}>
            <div className="px-6 pt-5 pb-2">
              <div className="text-[10px] font-bold uppercase tracking-widest text-blue-300 mb-1">Preisübersicht (Indikativ)</div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between text-blue-200">
                  <span>Basispreis</span><span className="font-semibold text-white">{fmt(preis.basispreis)}</span>
                </div>
                {preis.glasAufpreis > 0 && (
                  <div className="flex justify-between text-blue-200">
                    <span>Glasaufpreis</span><span className="font-semibold text-white">+{fmt(preis.glasAufpreis)}</span>
                  </div>
                )}
                {preis.rahmenAufpreis > 0 && (
                  <div className="flex justify-between text-blue-200">
                    <span>Farb-Aufpreis</span><span className="font-semibold text-white">+{fmt(preis.rahmenAufpreis)}</span>
                  </div>
                )}
                <div className="border-t border-white/10 my-2" />
                <div className="flex justify-between text-blue-200">
                  <span>Netto</span><span className="text-white">{fmt(preis.netto)}</span>
                </div>
                <div className="flex justify-between text-blue-200">
                  <span>MwSt. 19 %</span><span className="text-white">{fmt(preis.mwst)}</span>
                </div>
              </div>
            </div>
            <div className="mx-4 my-3 rounded-xl px-4 py-3 flex justify-between items-center"
              style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)' }}>
              <span className="text-white font-bold text-sm">Gesamt (brutto)</span>
              <span className="text-2xl font-black text-white">{fmt(preis.brutto)}</span>
            </div>
            <div className="px-4 pb-5 grid grid-cols-2 gap-3">
              <a href={`mailto:info@pergolaland.de?subject=Glasschiebewand-Anfrage&body=Breite: ${cfg.breite}mm%0AHöhe: ${cfg.hoehe}mm%0AScheiben: ${cfg.scheibenAnzahl}%0AGlastyp: ${GLAS_OPTIONEN.find(g=>g.id===cfg.glasTyp)?.name}%0AProfil: ${RAHMEN_OPTIONEN.find(r=>r.id===cfg.rahmenFarbe)?.name}${cfg.rahmenFarbe==='wunsch_ral'&&ralNummer?` (${ralNummer})`:''}%0AIndikativpreis: ${fmt(preis.brutto)}`}
                className="flex items-center justify-center gap-2 bg-white text-blue-700 font-bold text-sm rounded-xl py-3 hover:bg-blue-50 transition-all shadow-lg">
                <span>📧</span> Angebot anfragen
              </a>
              <button onClick={() => window.print()}
                className="flex items-center justify-center gap-2 font-bold text-sm rounded-xl py-3 transition-all border border-white/20 text-white hover:bg-white/10"
                style={{ background: 'rgba(255,255,255,0.08)' }}>
                <span>🖨️</span> PDF / Drucken
              </button>
            </div>
          </div>

          {/* Versandkosten-Hinweis */}
          <div className="flex items-start gap-2.5 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
            <span className="text-green-500 text-base mt-0.5">✓</span>
            <p className="text-xs text-green-700 leading-relaxed">
              Ihre Versandkosten wurden auf Basis des Gesamtgewichts <span className="font-semibold">(Weight)</span> und der Lieferdistanz <span className="font-semibold">(Distance)</span> erfolgreich validiert und berechnet.
            </p>
          </div>

        </div>

        {/* Rechte Spalte: Konfiguration */}
        <div className="space-y-4">

          {/* Abmessungen */}
          <Section title="Abmessungen">
            <div className="space-y-4">
              {/* Breite */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-gray-700">Breite</span>
                  <input type="number" min={1000} max={10000} step={10} value={cfg.breite}
                    onChange={e => upd({ breite: Math.min(10000, Math.max(1000, +e.target.value||1000)) })}
                    className="w-28 text-right font-bold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
                <input type="range" min={1000} max={10000} step={10} value={cfg.breite}
                  onChange={e => upd({ breite: +e.target.value })}
                  className="w-full h-1.5 rounded-full appearance-none accent-blue-500 cursor-pointer"
                  style={{ background: `linear-gradient(to right, #3b82f6 ${((cfg.breite-1000)/9000)*100}%, #e2e8f0 0%)` }}
                />
                <div className="flex justify-between text-[11px] text-gray-400 mt-1">
                  <span>1000 mm</span><span>10 000 mm</span>
                </div>
              </div>
              {/* Höhe */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-gray-700">Höhe</span>
                  <input type="number" min={800} max={2700} step={10} value={cfg.hoehe}
                    onChange={e => upd({ hoehe: Math.min(2700, Math.max(800, +e.target.value||800)) })}
                    className="w-28 text-right font-bold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
                <input type="range" min={800} max={2700} step={10} value={cfg.hoehe}
                  onChange={e => upd({ hoehe: +e.target.value })}
                  className="w-full h-1.5 rounded-full appearance-none accent-blue-500 cursor-pointer"
                  style={{ background: `linear-gradient(to right, #3b82f6 ${((cfg.hoehe-800)/1900)*100}%, #e2e8f0 0%)` }}
                />
                <div className="flex justify-between text-[11px] text-gray-400 mt-1">
                  <span>800 mm</span><span>2700 mm</span>
                </div>
              </div>
              {/* Auto Scheiben */}
              <div className="flex items-center gap-3 mt-1 p-3 rounded-xl"
                style={{ background: 'linear-gradient(135deg, #eff6ff, #e0f2fe)' }}>
                <div className="text-center flex-1">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-blue-400">Scheiben</div>
                  <div className="text-2xl font-black text-blue-700">{cfg.scheibenAnzahl}</div>
                </div>
                <div className="w-px h-10 bg-blue-200" />
                <div className="text-center flex-1">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-blue-400">je Scheibe</div>
                  <div className="text-2xl font-black text-blue-700">{scheibenBreite}</div>
                  <div className="text-[10px] text-blue-400">mm</div>
                </div>
                <div className="text-xs text-blue-400 flex-1 text-center leading-tight">
                  🔄 Auto<br/>70–105 cm
                </div>
              </div>
            </div>
          </Section>

          {/* Glastyp */}
          <Section title="Glastyp">
            <div className="grid grid-cols-2 gap-2">
              {GLAS_OPTIONEN.map(g => (
                <button key={g.id} onClick={() => upd({ glasTyp: g.id })}
                  className={`p-3 rounded-xl border-2 text-left transition-all duration-150 ${
                    cfg.glasTyp === g.id
                      ? 'border-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.15)]'
                      : 'border-gray-100 hover:border-gray-300'
                  }`}
                  style={{ background: cfg.glasTyp === g.id ? '#eff6ff' : '#fafafa' }}
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-6 h-6 rounded-lg shadow-inner border border-white/60"
                      style={{ backgroundColor: g.farbe, opacity: g.opacity + 0.45 }} />
                    <span className="text-sm font-semibold text-gray-800">{g.name}</span>
                  </div>
                  {cfg.glasTyp === g.id && (
                    <div className="mt-1.5 text-[10px] font-bold text-blue-500 uppercase tracking-wide">✓ Ausgewählt</div>
                  )}
                </button>
              ))}
            </div>
          </Section>

          {/* Profil-Farbe */}
          <Section title="Profil-Farbe">
            <div className="flex gap-3 flex-wrap mb-3">
              {RAHMEN_OPTIONEN.map(r => (
                <button key={r.id} onClick={() => upd({ rahmenFarbe: r.id })}
                  title={r.name}
                  className={`relative w-10 h-10 rounded-full transition-all duration-150 ${
                    cfg.rahmenFarbe === r.id ? 'scale-115 ring-2 ring-blue-500 ring-offset-2' : 'hover:scale-105'
                  }`}
                  style={r.hex === 'wunsch'
                    ? { background: 'conic-gradient(red,yellow,lime,cyan,blue,magenta,red)', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }
                    : { backgroundColor: r.hex, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }
                  }
                >
                  {cfg.rahmenFarbe === r.id && (
                    <span className="absolute inset-0 flex items-center justify-center text-white font-black text-xs drop-shadow">✓</span>
                  )}
                </button>
              ))}
            </div>
            <div className="text-xs text-gray-500 font-medium">
              {RAHMEN_OPTIONEN.find(r => r.id === cfg.rahmenFarbe)?.name}
              <span className="text-gray-400"> — {RAHMEN_OPTIONEN.find(r => r.id === cfg.rahmenFarbe)?.ral}</span>
            </div>
          </Section>

          {/* Wunsch RAL-Farbe */}
          <Section title="Wunsch RAL-Farbe">
            <input type="text" placeholder="z. B. RAL 6005"
              value={ralNummer} onChange={e => setRalNummer(e.target.value)}
              className="w-full border-2 border-blue-200 rounded-xl px-3 py-2.5 text-sm text-gray-800 bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-400 placeholder-gray-400 font-medium"
            />
            <p className="text-[11px] text-gray-400 mt-2">RAL-Nummer für individuelle Wunschfarbe eingeben (z. B. RAL 3020, RAL 5010 …)</p>
          </Section>

          {/* Öffnungsrichtung */}
          <Section title="Öffnungsrichtung">
            <div className="grid grid-cols-3 gap-2">
              {OEFFNUNG.map(([val, icon, label]) => {
                const disabled = nurBeidseitig && val !== 'beidseitig';
                return (
                  <button key={val}
                    onClick={() => !disabled && upd({ oeffnungsRichtung: val })}
                    disabled={disabled}
                    className={`py-3 rounded-xl border-2 text-center transition-all duration-150 ${
                      disabled
                        ? 'border-gray-100 opacity-35 cursor-not-allowed'
                        : cfg.oeffnungsRichtung === val
                          ? 'border-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.15)]'
                          : 'border-gray-100 hover:border-gray-300'
                    }`}
                    style={{ background: !disabled && cfg.oeffnungsRichtung === val ? '#eff6ff' : '#fafafa' }}
                  >
                    <div className="text-xl mb-0.5">{icon}</div>
                    <div className={`text-xs font-bold ${!disabled && cfg.oeffnungsRichtung === val ? 'text-blue-600' : 'text-gray-600'}`}>
                      {label}
                    </div>
                  </button>
                );
              })}
            </div>
            {nurBeidseitig && (
              <p className="text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
                ⚠️ Ab 530 cm Breite ist nur <strong>Beidseitig</strong> möglich.
              </p>
            )}

          </Section>

        </div>
      </div>

      <footer className="border-t border-gray-200 py-6 text-center text-xs text-gray-400"
        style={{ background: '#f0f2f5' }}>
        © 2025 PergolaLand · pergolaland.de · info@pergolaland.de<br />
        <span className="text-gray-300">Alle Preise sind unverbindliche Richtwerte inkl. MwSt.</span>
      </footer>
    </div>
  );
}
